import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AgentCitation, StreamedMemoryRecord } from '../../agents/agentStream.ts';
import type { SessionTranscriptTurn } from '../../agents/sessionApi.ts';
import {
  normalizeTier,
  type LogMemRecord,
  type SessionLogEvent,
  type ToolCallStatus,
} from '../../agents/sessionLog.ts';

/** A streamed `memory-write` op (append a turn's records, delete some, or reset). */
type MemoryWriteOp = 'append' | 'delete' | 'reset';

/** Drop the given record ids from a memory entry (other entries pass through). */
function scrubRecords(e: SessionLogEvent, ids: ReadonlySet<string>): SessionLogEvent {
  return e.type === 'memory-write' || e.type === 'memory-recall'
    ? { ...e, records: e.records.filter((r) => !ids.has(r.id)) }
    : e;
}

/** Record ids already present as recall entries (for de-duping new recalls). */
function recallRecordIds(e: SessionLogEvent): readonly string[] {
  return e.type === 'memory-recall' ? e.records.map((r) => r.id) : [];
}

export interface SessionLogApi {
  /** The ordered, append-only log. The memory graph is a pure projection of it. */
  readonly events: readonly SessionLogEvent[];
  /** A new user turn begins. */
  readonly appendUserMessage: (text: string) => void;
  /** A tool call settled this turn. */
  readonly appendToolCall: (
    name: string,
    args: Record<string, unknown>,
    output: string,
    status?: ToolCallStatus,
  ) => void;
  /** A streamed `memory-write` frame (append/delete/reset). */
  readonly applyMemoryWrite: (
    moduleId: string,
    op: MemoryWriteOp,
    records: readonly StreamedMemoryRecord[] | undefined,
    recordIds: readonly string[] | undefined,
  ) => void;
  /** Records the agent recalled into context this turn, from the runtime's
   *  structured `memory-recall` stream frame (real ids + tier). */
  readonly appendMemoryRecall: (
    moduleId: string,
    records: readonly StreamedMemoryRecord[],
  ) => void;
  /** The turn's reply text settled. */
  readonly appendAssistantMessage: (text: string, citations?: readonly AgentCitation[]) => void;
}

function nowIso(): string {
  return new Date().toISOString();
}

/** Restore: rebuild the log from the saved transcript. A turn's tools are surfaced
 *  as a single aggregated tool node (from the one-line `toolSummary`), and the
 *  records the agent recalled that turn are relit from the lossless `recalled`
 *  field the runtime persists — so a restored session shows the cross-tier records
 *  that were in context, with their real ids, just like it did live. (Older
 *  transcripts without `recalled` simply restore without recall nodes.) */
function buildFromTranscript(
  turns: readonly SessionTranscriptTurn[],
  mkId: () => string,
): SessionLogEvent[] {
  const out: SessionLogEvent[] = [];
  for (const turn of turns) {
    if (turn.role === 'user') {
      out.push({ type: 'user-message', id: mkId(), ts: turn.ts, text: turn.text });
      continue;
    }
    const summary = turn.toolSummary?.trim();
    if (summary) {
      out.push({ type: 'tool-call', id: mkId(), ts: turn.ts, name: 'tools', args: {}, output: summary, status: 'ok' });
    }
    for (const recall of turn.recalled ?? []) {
      const tier = normalizeTier(recall.moduleId);
      const records: LogMemRecord[] = recall.records.map((r) => ({
        id: r.id,
        tier,
        kind: r.kind,
        text: r.text,
        ts: r.ts,
      }));
      if (records.length > 0) {
        out.push({ type: 'memory-recall', id: mkId(), ts: turn.ts, records });
      }
    }
    out.push({ type: 'assistant-message', id: mkId(), ts: turn.ts, text: turn.text });
  }
  return out;
}

/**
 * Owns the session log (see `sessionLog.ts`) — the single source the memory graph
 * projects from. Hydrates from the restored transcript on (session, restoreKey)
 * change, and exposes append methods that {@link useAgentChat} calls as a turn
 * streams. The graph is reconciled from `events` by `logToGraph` (in AgentConsole).
 *
 * Distinct from `useAgentMemory`, which owns the **knowledge base** (the full
 * `/agent-memory` store) feeding the "All" list — a separate source by design.
 */
export function useSessionLog(
  sessionId: string | undefined,
  transcript: readonly SessionTranscriptTurn[] | null | undefined,
  restoreKey: number | undefined,
): SessionLogApi {
  const [events, setEvents] = useState<readonly SessionLogEvent[]>([]);
  const seq = useRef(0);
  const mkId = useCallback((): string => `e${seq.current++}`, []);

  // Rehydrate the log when the session changes (or the same session's transcript
  // is re-restored on reload). Read the transcript via ref so the effect keys only
  // on identity, matching useAgentChat's reset.
  const transcriptRef = useRef(transcript);
  transcriptRef.current = transcript;
  useEffect(() => {
    seq.current = 0;
    const t = transcriptRef.current;
    setEvents(t && t.length > 0 ? buildFromTranscript(t, mkId) : []);
  }, [sessionId, restoreKey, mkId]);

  const appendUserMessage = useCallback(
    (text: string): void => {
      setEvents((prev) => [...prev, { type: 'user-message', id: mkId(), ts: nowIso(), text }]);
    },
    [mkId],
  );

  const appendToolCall = useCallback(
    (name: string, args: Record<string, unknown>, output: string, status: ToolCallStatus = 'ok'): void => {
      setEvents((prev) => [
        ...prev,
        { type: 'tool-call', id: mkId(), ts: nowIso(), name, args, output, status },
      ]);
    },
    [mkId],
  );

  const applyMemoryWrite = useCallback<SessionLogApi['applyMemoryWrite']>(
    (moduleId, op, records, recordIds) => {
      setEvents((prev) => {
        if (op === 'reset') {
          return prev.filter((e) => e.type !== 'memory-write' && e.type !== 'memory-recall');
        }
        if (op === 'delete') {
          const ids = new Set(recordIds ?? []);
          if (ids.size === 0) {
            return prev;
          }
          return prev
            .map((e) => scrubRecords(e, ids))
            .filter((e) => (e.type === 'memory-write' || e.type === 'memory-recall' ? e.records.length > 0 : true));
        }
        const tier = normalizeTier(moduleId);
        const mapped: LogMemRecord[] = (records ?? []).map((r) => ({
          id: r.id,
          tier,
          kind: r.kind,
          text: r.text,
          ts: r.ts,
        }));
        if (mapped.length === 0) {
          return prev;
        }
        return [...prev, { type: 'memory-write', id: mkId(), ts: nowIso(), records: mapped }];
      });
    },
    [mkId],
  );

  const appendMemoryRecall = useCallback(
    (moduleId: string, records: readonly StreamedMemoryRecord[]): void => {
      const tier = normalizeTier(moduleId);
      const mapped: LogMemRecord[] = records.map((r) => ({
        id: r.id,
        tier,
        kind: r.kind,
        text: r.text,
        ts: r.ts,
      }));
      if (mapped.length === 0) {
        return;
      }
      setEvents((prev) => {
        // De-dup by record id: a record already recalled this session keeps its
        // existing node rather than re-adding it each turn it stays in context.
        const have = new Set(prev.flatMap(recallRecordIds));
        const fresh = mapped.filter((r) => !have.has(r.id));
        if (fresh.length === 0) {
          return prev;
        }
        return [...prev, { type: 'memory-recall', id: mkId(), ts: nowIso(), records: fresh }];
      });
    },
    [mkId],
  );

  const appendAssistantMessage = useCallback(
    (text: string, citations?: readonly AgentCitation[]): void => {
      setEvents((prev) => [
        ...prev,
        {
          type: 'assistant-message',
          id: mkId(),
          ts: nowIso(),
          text,
          ...(citations && citations.length > 0 ? { citations } : {}),
        },
      ]);
    },
    [mkId],
  );

  return useMemo(
    () => ({
      events,
      appendUserMessage,
      appendToolCall,
      applyMemoryWrite,
      appendMemoryRecall,
      appendAssistantMessage,
    }),
    [events, appendUserMessage, appendToolCall, applyMemoryWrite, appendMemoryRecall, appendAssistantMessage],
  );
}
